import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Table, Dropdown, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    SortDownAsc,
    SortDownDesc,
    SolidRemoveCross,
} from './icons/Icons';
import { ConditionalRendering } from '../conditional-rendering';
import { PaginationUI } from '../react-table/PaginationUI';
import { HIDE_COLUMN, SORT } from '../../../modules/admin/constants/enum';

interface StyledContainerProps {
    children: ReactNode;
}
const PaginationContainer = styled.div<StyledContainerProps>`
    display: grid;
    gap: 16px;
    max-width: 100%;
    position: relative;
    margin: auto;
    margin-top: 24px;

    /* Mobile: stack items */
    grid-template-columns: 1fr;

    @media (min-width: 768px) {
        /* Desktop: three columns */
        grid-template-columns: repeat(3, 1fr);

        /* Align the three items */
        & > *:nth-child(1) {
            justify-self: start;
        }
        & > *:nth-child(2) {
            justify-self: center;
        }
        & > *:nth-child(3) {
            justify-self: end;
        }
    }
`;

interface StyledDataRangeProps {
    mt?: number;
    children: ReactNode;
}

const StyledDataRange = styled.p<StyledDataRangeProps>`
    color: #718096;
    margin: 4px;
`;

interface Column<T> {
    key: keyof T;
    label: string;
    render?: (item: T) => ReactElement | null;
}

interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    pageSize?: number;
    currentPage?: number;
    totalPages?: number;
    totalItems: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    enableSorting?: boolean;
    enableColumnHiding?: boolean;
    enablePageJump?: boolean;
    hideItemCount?: boolean;
    hideRowsPerPage?: boolean;
    startPageIndex?: number;
    hidePageControl?: boolean;
    gethidenColumnList?: Function;
    resetColumn?: number,
    visiblePageNumbers?: number;
}

const DropdownItemContent = ({ icon: Icon, text }: { icon: React.ReactNode; text: React.ReactNode }) => (
    <span>
        {Icon}
        <span className="span-separator">{text}</span>
    </span>
);

const ROWS_PER_PAGE = [5, 10, 20];

export const renderCellContent = (content?: string | null, styles: object = {}) => {
    if (!content) {
        return null;
    }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${content}`} data-testid={`tooltip-${content}`}>{content}</Tooltip>}>
            <span
                className="d-inline-block cell-content"
                style={{ ...styles }}
                data-testid={`cell-content-${content}`}
            >
                {content}
            </span>

        </OverlayTrigger>
    );
};

const PaginatedTable = <T extends Record<string, any>>({
    data = [],
    columns,
    pageSize = 10,
    currentPage = 0,
    totalPages = 1,
    totalItems = data?.length,
    onPageChange = () => {},
    onPageSizeChange = () => {},
    enableSorting = true,
    enableColumnHiding = true,
    hideItemCount = false,
    hideRowsPerPage = false,
    startPageIndex = 1,
    hidePageControl = true,
    gethidenColumnList,
    resetColumn = 0,
}: DataTableProps<T>) => {
    const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
    const [hiddenColumns, setHiddenColumns] = useState<Set<keyof T>>(new Set());
    const start = currentPage * pageSize + 1;
    const end = start + data?.length - 1;
    const isZeroBased = startPageIndex === 0;

    const { firstPage, lastPage, displayPage, pageNumbersToShow } = getPaginationBounds(startPageIndex, totalPages, currentPage);

    useEffect(() => {
        if (resetColumn) {
            setHiddenColumns(new Set());
        }
    }, [resetColumn]);

    const handleSort = (column: keyof T) => {
        if (!enableSorting) return;
        setSortColumn(column);
        setSortOrder(sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const removeSort = (): void => {
        if (!enableSorting) return;
        setSortColumn(null);
        setSortOrder(null);
    };

    const toggleColumnVisibility = (column: keyof T): void => {
        if (!enableColumnHiding) return;
    
        const toggleColumn = (prev: Set<keyof T>): Set<keyof T> => {
            const newHidden = new Set(prev);
            newHidden.has(column) ? newHidden.delete(column) : newHidden.add(column);
            return newHidden;
        };
    
        setHiddenColumns(toggleColumn);
    
        if (gethidenColumnList) {
            gethidenColumnList(toggleColumn);
        }
    };

    const paginatedData = [...data].sort((a, b) => {
        if (!sortColumn) return 0;
        const valueA = a[sortColumn];
        const valueB = b[sortColumn];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
            return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
        }
        if (typeof valueA === 'number' && typeof valueB === 'number') {
            return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        }
        return 0;
    });

    function getPaginationBounds(
        startPageIndex: number,
        totalPages: number,
        currentPage: number,
        windowSize = 3
    ) {    
        const firstPage = startPageIndex;
        const lastPage = isZeroBased ? totalPages - 1 : totalPages;
        const displayPage = isZeroBased ? currentPage + 1 : currentPage;
    
        // Calculate visible page numbers for UI (always 1-based)
        const displayCurrentPage = displayPage;
        const half = Math.floor(windowSize / 2);
        let start = displayCurrentPage - half;
        let end = displayCurrentPage + half;
    
        if (start < 1) {
            start = 1;
            end = Math.min(windowSize, totalPages);
        }
    
        if (end > totalPages) {
            end = totalPages;
            start = Math.max(1, end - windowSize + 1);
        }
    
        const pageNumbersToShow: number[] = [];
        for (let i = start; i <= end; i++) {
            pageNumbersToShow.push(i);
        }
    
        return {
            firstPage,
            lastPage,
            displayPage,
            pageNumbersToShow,
        };
    }    

    const renderRow = (row, index): React.ReactNode => (
        <tr key={index} data-testid={`table-row-${index}`}>
            {columns
                .filter(col => !hiddenColumns.has(col.key))
                .map(col => {
                    const cellContent = col?.render?.(row) ?? renderCellContent(row[col.key]);
                    return (
                        <td key={String(col.key)} data-testid={`table-cell-${index}-${String(col.key)}`}>
                            {cellContent}
                        </td>
                    );
                })}
        </tr>
    );

    const renderColumn = (col): React.ReactNode => {
        const enableColumnSort = !col?.filterOptions?.length || col.filterOptions.some(value => value === SORT);

        const enableColumnHide = !col?.filterOptions?.length || col.filterOptions.some(value => value === HIDE_COLUMN);

        return (
            <th key={col.key as string} data-testid={`column-header-${String(col.key)}`}>
                <ConditionalRendering isTrue={enableSorting || enableColumnHiding}>
                    <Dropdown data-testid={`column-dropdown-${String(col.key)}`}>
                        <Dropdown.Toggle
                            variant="white"
                            className="column-options"
                            data-testid={`column-dropdown-toggle-${String(col.key)}`}
                        >
                            <span className="table-heading">
                                {col.label}
                                <ConditionalRendering isTrue={enableSorting && sortColumn === col.key}>
                                    <span className="sort-indicator">
                                        <FontAwesomeIcon
                                            icon={sortOrder === 'asc' ? 'sort-up' : 'sort-down'}
                                            size="sm"
                                            data-testid={`sort-icon-${sortOrder}`}
                                        />
                                    </span>
                                </ConditionalRendering>
                            </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu data-testid={`column-dropdown-menu-${String(col.key)}`}>
                            <ConditionalRendering isTrue={enableSorting && enableColumnSort}>
                                <Dropdown.Item
                                    onClick={() => handleSort(col.key)}
                                    data-testid={`sort-asc-${String(col.key)}`}
                                >
                                    <DropdownItemContent
                                        icon={<SortDownAsc />}
                                        text={<FormattedMessage id="common.table.header.sortAscending" />}
                                    />
                                </Dropdown.Item>
                                <Dropdown.Divider />

                                <Dropdown.Item
                                    onClick={() => handleSort(col.key)}
                                    data-testid={`sort-desc-${String(col.key)}`}
                                >
                                    <DropdownItemContent
                                        icon={<SortDownDesc />}
                                        text={<FormattedMessage id="common.table.header.sortDescending" />}
                                    />
                                </Dropdown.Item>
                                <Dropdown.Divider />

                                <ConditionalRendering isTrue={!!sortOrder}>
                                    <Dropdown.Item onClick={removeSort} data-testid={`sort-remove-${String(col.key)}`}>
                                        <DropdownItemContent
                                            icon={<SolidRemoveCross />}
                                            text={<FormattedMessage id="common.table.header.removeSort" />}
                                        />
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                </ConditionalRendering>
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={enableColumnHiding && enableColumnHide}>
                                <Dropdown.Item
                                    onClick={() => toggleColumnVisibility(col.key)}
                                    data-testid={`hide-column-${String(col.key)}`}
                                >
                                    <DropdownItemContent
                                        icon={<SolidRemoveCross />}
                                        text={<FormattedMessage id="common.table.header.hideColumn" />}
                                    />
                                </Dropdown.Item>
                            </ConditionalRendering>
                        </Dropdown.Menu>
                    </Dropdown>
                </ConditionalRendering>
                <ConditionalRendering isTrue={!(enableSorting || enableColumnHiding)}>
                    <span className="table-heading">{col.label}</span>
                </ConditionalRendering>
            </th>
        );
    };


    return (
        <div className="table-wrapper" data-testid="paginated-table-wrapper">
            <div className="pagination-table-responsive">
                <Table striped bordered hover className="fixed-table" data-testid="paginated-table">
                    <thead data-testid="table-header">
                        <tr data-testid="table-header-row">
                            {columns
                                .filter(col => !hiddenColumns.has(col.key))
                                .map(col => (
                                    renderColumn(col)
                                ))}
                        </tr>
                    </thead>
                    <tbody className="table-container">
                        {paginatedData?.map((row, index) => renderRow(row, index))}
                    </tbody>
                </Table>
            </div>
            <ConditionalRendering isTrue={!hidePageControl}>
                <PaginationContainer data-testid="pagination">
                    <ConditionalRendering isTrue={!hideItemCount}>
                        <StyledDataRange data-testid="data-range">
                            <span>{`${start} - ${end} of ${totalItems} items`}</span>
                        </StyledDataRange>
                    </ConditionalRendering>
                    <PaginationUI
                        pages={pageNumbersToShow}
                        onPageChange={page => onPageChange(isZeroBased ? page - 1 : page)}
                        isPrevDisabled={currentPage === firstPage}
                        isActive={page => displayPage === page}
                        isNextDisabled={currentPage === lastPage}
                        onPrev={() => onPageChange(Math.max(firstPage, currentPage - 1))}
                        onNext={() => onPageChange(Math.min(lastPage, currentPage + 1))}
                        goToFirstPage={() => onPageChange(firstPage)}
                        goToLastPage={() => onPageChange(lastPage)}
                        isFirstAndLastNavigation={true}
                        data-testid="pagination-ui"
                    />
                    <ConditionalRendering isTrue={!hideRowsPerPage}>
                        <div className="d-flex align-items-center" data-testid="rows-per-page-container">
                            <Form.Control
                                as="select"
                                value={pageSize}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    console.log('In Dropdown: handleValueChange(): val = ', e?.target?.value);
                                    return onPageSizeChange(Number(e?.target?.value));
                                }}
                                className="items-per-page"
                                data-testid="rows-per-page-select"
                            >
                                {ROWS_PER_PAGE.map(size => (
                                    <option key={size} value={size} data-testid={`rows-per-page-option-${size}`}>
                                        {size}
                                    </option>
                                ))}
                            </Form.Control>
                            <StyledDataRange data-testid="rows-per-page-label">
                                <span>items per page</span>
                            </StyledDataRange>
                        </div>
                    </ConditionalRendering>
                </PaginationContainer>
            </ConditionalRendering>
        </div>
    );
};

export default PaginatedTable;
